import axios from "axios";

// Global Variables
import { ApiReqBaseUrl } from "../assets/GlobalVariables";
import { setUserInfo, getUserInfo, getSavedCredentials, saveCredentials } from "./localStorage";
import { performPostFetch } from "./fetchService";
import {
  parseResponseData,
  triggerInvalidSessionLogout,
} from "./sessionInvalidation";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import { TRIPMASTER_ACCESS_STORAGE_KEY } from "../features/vacationPackagesPublic/tripMasterAccessLogic";

// Authenticated User
const getAuthenticatedUser = () => {
  // Use getUserInfo which handles decryption automatically
  // This is important because UserInfo is encrypted in localStorage
  return getUserInfo();
};

/** Agent id from session user object or localStorage fallback (set on login). */
export function resolveSessionAgentId(user = getAuthenticatedUser()) {
  if (user && typeof user === "object") {
    const id = user.id ?? user.agentId ?? user.agent_id ?? user.agentid;
    if (id != null && String(id).trim() !== "") return id;
  }
  if (typeof localStorage !== "undefined") {
    try {
      const fromLs = localStorage.getItem("agentId");
      if (fromLs && String(fromLs).trim()) return fromLs;
    } catch {
      /* ignore */
    }
  }
  return null;
}

/** Active session id from UserInfo or localStorage `sessionID` key. */
export function resolveSessionIdSync() {
  const user = getAuthenticatedUser();
  if (user && typeof user === "object") {
    const sid = user.sessionID ?? user.sessionId;
    if (sid != null && String(sid).trim() !== "") return String(sid).trim();
  }
  if (typeof localStorage !== "undefined") {
    try {
      const ls = localStorage.getItem("sessionID");
      if (ls != null && String(ls).trim() !== "") return String(ls).trim();
    } catch {
      /* ignore */
    }
  }
  return null;
}

/** Agent session or HR admin may send LDS flight PROPOSAL. */
export function canSubmitLdsFlightProposal(user = getAuthenticatedUser()) {
  return (
    Boolean(resolveSessionAgentId(user)) ||
    isAdminUser() ||
    Boolean(resolveSessionIdSync())
  );
}

const persistAuthenticatedUser = (userData) => {
  // Save user info to localStorage (will be encrypted automatically)
  setUserInfo(userData);
  // כמו בפרויקט הישן: שמירת sessionID גם תחת מפתח נפרד (גיבוי + תאימות)
  const sid = userData.sessionID ?? userData.sessionId;
  if (sid) {
    localStorage.setItem('sessionID', typeof sid === 'string' ? sid : String(sid));
    if (process.env.NODE_ENV === 'development') {
      console.log('[authService] Saved sessionID to localStorage (key: sessionID)');
    }
  }
  if (userData.id != null) {
    localStorage.setItem('agentId', String(userData.id));
  }
};

const persistLoginResponse = (data) => {
  try {
    persistAuthenticatedUser(data);
    return { status: "ok" };
  } catch (saveError) {
    console.error('[authService] Error saving user info:', saveError);
    // Even if save fails, return ok if login was successful
    return { status: "ok" };
  }
};

const handleServerInactiveSession = async () => {
  await triggerInvalidSessionLogout();
};

// Middleware to check if the user is authenticated
const isAuthenticated = async () => {
  const user = getAuthenticatedUser();
  if (user && user.sessionID) {
    try {
      const res = await axios.post(
        `${ApiReqBaseUrl}/isExpired`,
        { sessionID: user.sessionID },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );
      const payload = parseResponseData(res?.data);
      if (payload && payload.active === true) {
        return true;
      }
      await handleServerInactiveSession();
      return false;
    } catch (err) {
      if (err.response) {
        const status = err.response.status;
        if (status === 401 || status === 403) {
          await handleServerInactiveSession();
          return false;
        }
        if (status === 400) {
          return false;
        }
        return true;
      }
      if (err.request) {
        return true;
      }
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        return true;
      }
      return true;
    }
  }
  return false;
};

// Login endpoint
const login = async (data) => {
  try {
    const res = await performPostFetch(`${ApiReqBaseUrl}/login`, data);
    const payload = res?.data || res;

    console.log("[LOGIN_2FA_FRONTEND_RESPONSE_TRACE]", {
      status: payload?.status,
      requires_2fa: payload?.requires_2fa,
      hasVerificationSession: Boolean(payload?.verification_session),
      masked_email: payload?.masked_email,
    });

    if (process.env.NODE_ENV === "development") {
      console.log("[AUTH_LOGIN_RESPONSE_SHAPE_TRACE]", {
        hasDataWrapper: Boolean(res?.data),
        requires2fa: Boolean(payload?.requires_2fa),
        status: payload?.status,
      });
    }

    if (payload) {
      if (payload.requires_2fa === true || payload.status === "requires_2fa") {
        return {
          status: "requires_2fa",
          requires_2fa: true,
          verification_session: payload.verification_session,
          masked_email: payload.masked_email,
          can_resend: payload.can_resend,
          resend_available_at: payload.resend_available_at,
          message: payload.message,
        };
      }

      const hasStatusOk = payload.status === "ok";
      const hasUserInfo = payload.email && payload.id;

      if (hasStatusOk || hasUserInfo) {
        return persistLoginResponse(payload);
      }

      if (payload.status === "fail" || payload.error) {
        return { status: "fail", message: payload.error || payload.message };
      }
    }

    return { status: "fail", message: "Unexpected response from server" };
  } catch (error) {
    console.error('[authService] Login error:', error);
    return { status: "fail", message: error.message || "Login failed" };
  }
};

const verifyLoginCode = async (verification_session, code) => {
  try {
    const res = await performPostFetch(`${ApiReqBaseUrl}/auth/verify-login-code`, {
      verification_session,
      code,
    });

    if (res && res.data) {
      const hasStatusOk = res.data.status === "ok";
      const hasUserInfo = res.data.email && res.data.sessionID;
      if (hasStatusOk || hasUserInfo) {
        return persistLoginResponse(res.data);
      }
      return {
        status: "fail",
        message: res.data.message || res.data.error || "Invalid verification code",
      };
    }
    return { status: "fail", message: "Unexpected response from server" };
  } catch (error) {
    console.error('[authService] Verify login code error:', error);
    return { status: "fail", message: error.message || "Verification failed" };
  }
};

const resendLoginCode = async (verification_session) => {
  try {
    const res = await performPostFetch(`${ApiReqBaseUrl}/auth/resend-login-code`, {
      verification_session,
    });

    if (res && res.data) {
      if (res.data.status === "ok" || res.data.requires_2fa === true) {
        return {
          status: "ok",
          requires_2fa: true,
          verification_session: res.data.verification_session,
          masked_email: res.data.masked_email,
          can_resend: res.data.can_resend,
          resend_available_at: res.data.resend_available_at,
          message: res.data.message,
        };
      }
      return {
        status: "fail",
        message: res.data.message || res.data.error || "Could not resend code",
        can_resend: res.data.can_resend,
        resend_available_at: res.data.resend_available_at,
      };
    }
    return { status: "fail", message: "Unexpected response from server" };
  } catch (error) {
    console.error('[authService] Resend login code error:', error);
    return { status: "fail", message: error.message || "Could not resend code" };
  }
};

const resetPasswordRequest = async (email) => {
  const res = await performPostFetch(`${ApiReqBaseUrl}/resetPassword`, {
    email,
  });
  return res.data;
};

const resetPassword = async (password, token) => {
  const res = await performPostFetch(`${ApiReqBaseUrl}/changePassword`, {
    password,
    token,
  });
  return res.data;
};

const changeUserImage = async (id, image) => {
  return performPostFetch(`${ApiReqBaseUrl}/changeProfileImage`, {
    id,
    base64Image: image,
  });
};
const changeUserLogo = async (id, image) => {
  return performPostFetch(`${ApiReqBaseUrl}/changeLogo`, {
    id,
    base64Image: image,
  });
};

// Logout endpoint
const logout = async () => {
  try {
    const user = getAuthenticatedUser();
    
    // Save saved credentials before clearing localStorage (if they exist)
    // This preserves the "Remember Me" functionality
    const savedCredentials = getSavedCredentials();
    let tripMasterGate = null;
    try {
      if (isTripMasterSurface()) {
        tripMasterGate = localStorage.getItem(TRIPMASTER_ACCESS_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
    
    // Remove agent activity time and session keys (תאימות לפרויקט הישן)
    localStorage.removeItem('agentLastActivityTime');
    localStorage.removeItem('sessionID');
    localStorage.removeItem('agentId');

    // Remove all data from the local storage and session storage
    // localStorage.removeItem('UserInfo');
    localStorage.clear();
    sessionStorage.clear();

    if (tripMasterGate) {
      try {
        localStorage.setItem(TRIPMASTER_ACCESS_STORAGE_KEY, tripMasterGate);
      } catch {
        /* ignore */
      }
    }

    // Restore saved credentials if they existed (for "Remember Me" functionality)
    if (savedCredentials && savedCredentials.email && savedCredentials.password) {
      saveCredentials(savedCredentials.email, savedCredentials.password);
    }

    if (user && user.sessionID) {
      let data = {
        sessionID: user.sessionID,
      };

      // Log user out
      return await axios.post(`${ApiReqBaseUrl}/logout`, data, {
        withCredentials: true,
      });
    }
  } catch (err) {
    console.log({ err });
  }
};

// Endpoint to get authenticated user details
const getUserDetails = () => {
  if (getAuthenticatedUser() !== null) {
    // Return user
    return getAuthenticatedUser();
  } else {
    return false;
  }
};

const normalizeAdminFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1";
  }
  return false;
};

const normalizeRole = (value) => String(value || "").trim().toLowerCase();

/** Internal HR commission (margin) — admin / super_admin only. */
const canViewHrCommission = (user = getAuthenticatedUser()) => {
  if (!user || typeof user !== "object") return false;
  const role = normalizeRole(user.role ?? user.agentRole);
  return role === "admin" || role === "super_admin";
};

/** Hotels River agency account — not other agents on the platform. */
const HOTELS_RIVER_AGENCY_AGENT_IDS = new Set(["5413"]);
const HOTELS_RIVER_AGENCY_EMAILS = new Set(["adir@hotelsriver.com"]);

/**
 * True only for the Hotels River agency login (agent 5413 / adir@hotelsriver.com).
 */
const isHotelsRiverAgencyAccount = (user = getAuthenticatedUser()) => {
  const agentId = resolveSessionAgentId(user);
  if (agentId != null && HOTELS_RIVER_AGENCY_AGENT_IDS.has(String(agentId).trim())) {
    return true;
  }
  if (!user || typeof user !== "object") return false;
  const email = String(user.email ?? user.mail ?? user.userEmail ?? "")
    .trim()
    .toLowerCase();
  return Boolean(email && HOTELS_RIVER_AGENCY_EMAILS.has(email));
};

const isHotelsRiverAgencyTeamLeadId = (teamLeadAgentId) => {
  if (teamLeadAgentId == null || String(teamLeadAgentId).trim() === "") return false;
  return HOTELS_RIVER_AGENCY_AGENT_IDS.has(String(teamLeadAgentId).trim());
};

const resolveTeamLeadAgentIdFromAccountOverview = (overview) => {
  if (!overview || typeof overview !== "object") return null;
  return (
    overview.team?.team_lead_agent_id ??
    overview.agent?.team_lead_agent_id ??
    null
  );
};

/** Dashboard KPI: system-wide עמלות HR — Hotels River agency only. */
const canViewHrCommissionSystemKpi = (user = getAuthenticatedUser()) =>
  isHotelsRiverAgencyAccount(user);

/**
 * Mano target/bonus board: Hotels River agency login, or agents under that agency team.
 */
const canViewManoTargetStatus = (user = getAuthenticatedUser(), extras = {}) => {
  if (isHotelsRiverAgencyAccount(user)) return true;
  const leadId =
    extras?.teamLeadAgentId ??
    extras?.team_lead_agent_id ??
    resolveTeamLeadAgentIdFromAccountOverview(extras?.accountOverview) ??
    (user && typeof user === "object"
      ? user.team_lead_agent_id ?? user.teamLeadAgentId
      : null);
  return isHotelsRiverAgencyTeamLeadId(leadId);
};


const isAdminUser = () => {
  const user = getAuthenticatedUser();
  if (!user || typeof user !== "object") return false;

  // SSOT admin checks:
  // is_admin === true | 1 | "true" | "1"
  const hasAdminFlag = normalizeAdminFlag(
    user.is_admin ?? user.isAdmin ?? user.admin ?? user.isAdminUser
  );

  // role === admin | super_admin
  const role = normalizeRole(user.role ?? user.agentRole);
  const hasAdminRole = role === "admin" || role === "super_admin";

  // usertype: platform SSOT is "admins" (plural); also accept admin | super_admin
  const usertype = normalizeRole(user.usertype ?? user.userType);
  const hasAdminUsertype =
    usertype === "admins" ||
    usertype === "admin" ||
    usertype === "super_admin";

  return hasAdminFlag || hasAdminRole || hasAdminUsertype;
};

/** Admin dashboard + J5 pending captures (includes operation desk role). */
const canAccessJ5PendingCaptures = () => {
  const user = getAuthenticatedUser();
  if (!user || typeof user !== "object") return false;
  if (isAdminUser()) return true;
  const role = normalizeRole(user.role ?? user.agentRole);
  return role === "operation";
};

/** Only adir@hotelsriver.com (SUPER_ADMIN) may manage demotable admin roles in dashboard. */
const isSuperAdminUser = () => {
  const user = getAuthenticatedUser();
  if (!user || typeof user !== "object") return false;
  const email = String(user.email ?? user.userEmail ?? "").trim().toLowerCase();
  const superAdminEmail = String(
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_SUPER_ADMIN_EMAIL) ||
      (typeof process !== "undefined" && process.env?.REACT_APP_SUPER_ADMIN_EMAIL) ||
      "adir@hotelsriver.com",
  )
    .trim()
    .toLowerCase();
  if (email && superAdminEmail && email === superAdminEmail) return true;
  const role = normalizeRole(user.role ?? user.agentRole);
  return role === "super_admin";
};

const getSessionId = async () => {
  const user = getAuthenticatedUser();
  const fromUser = user?.sessionID ?? user?.sessionId ?? null;
  const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('sessionID') : null;
  const sessionID = fromUser || fromStorage || false;

  if (process.env.NODE_ENV === 'development' && !sessionID) {
    console.log('[authService] getSessionId: no session', {
      hasUser: !!user,
      userKeys: user ? Object.keys(user) : [],
      hasSessionID: !!(user?.sessionID),
      hasSessionId: !!(user?.sessionId),
      fromStorage: !!fromStorage,
      localStorageKeys: typeof localStorage !== 'undefined' ? Object.keys(localStorage).filter(k => /session|user|agent/i.test(k)) : [],
    });
  }

  return sessionID || false;
};

// Idle activity timestamps are not session enforcement. The server 3-hour TTL is SSOT.
const updateAgentActivityTime = () => {};

const isAgentSessionExpired = () => false;

const getAgentSessionRemainingTime = () => Infinity;

export {
  isAuthenticated,
  login,
  verifyLoginCode,
  resendLoginCode,
  logout,
  getUserDetails,
  isAdminUser,
  isHotelsRiverAgencyAccount,
  isHotelsRiverAgencyTeamLeadId,
  resolveTeamLeadAgentIdFromAccountOverview,
  canViewHrCommission,
  canViewHrCommissionSystemKpi,
  canViewManoTargetStatus,
  isSuperAdminUser,
  canAccessJ5PendingCaptures,
  getSessionId,
  changeUserImage,
  changeUserLogo,
  resetPasswordRequest,
  resetPassword,
  isAgentSessionExpired,
  getAgentSessionRemainingTime,
  updateAgentActivityTime,
};
